import { Button } from '@cluey/cluey-components';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import { submitCaseToSF } from '../../../actions/hub/hubContactPageActions';
import { ReactComponent as CheckIcon } from '../../../assets/images/icon-outline-checked.svg';
import ErrorBlock from '../../../common/ErrorBlock';
import { ContainerLayout } from '../../../common/hub/ContainerLayout';
import EnrolmentLayout from '../../../common/hub/EnrolmentLayout';
import withSelectedEnrolment from '../../../common/hub/NewWithSelectedEnrolment';
import { RestrictedButton } from '../../../components/RestrictedButton/RestrictedButton';
import { ShadowContainer } from '../../../components/ShadowContainer/ShadowContainer';
import { TextArea, useTextAreaProps } from '../../../components/TextArea/TextArea';
import { PATH_HUB_CR_CHANGE_CONTENT_FOCUS, PATH_HUB_ENROLMENT_DETAIL } from '../../../util/pagePath';
import { usePageHeader } from '../../../v2/app-shell/usePageHeader';

const formName = 'contact-form-change-content-focus';

const ViewTypes = {
  Default: 'default',
  Success: 'success',
};

const ErrorTypes = {
  TextAreaMessage: 'textAreaMessage',
};

const FormChangeContentFocus = ({ selectedEnrolment, isSubmitting, submitError, errorMsg, submit }) => {
  const [view, setView] = useState(ViewTypes.Default);
  const [errors, setErrors] = useState([]);
  const { setHeader } = usePageHeader();
  const history = useHistory();

  useEffect(() => {
    setHeader({
      label: 'Content and topic focus request',
      showIcon: false,
      showBackButton: true,
    });
  }, [setHeader]);

  const textAreaProps = useTextAreaProps({
    id: `${formName}-textarea`,
    labelText: 'Message',
    placeholder: "Give us some detail on how you'd like to focus learning.",
  });

  const textAreaIsEmpty = () => !textAreaProps.value || !textAreaProps.value.replace(/\s/g, '').length;

  const formIsValid = () => {
    if (textAreaIsEmpty()) {
      return false;
    }

    return true;
  };

  const { enrolmentId, student, subject } = selectedEnrolment;

  // Clear error message if form is valid
  if (formIsValid() && errors.length) {
    setErrors([]);
  }

  const submitPayloadToSF = () => {
    const uniqueEnrolmentId = enrolmentId;
    const caseType = 'Support';
    const caseCategory = 'Support request';
    const caseSubCategory = 'Content request/support for student';
    const caseRequestFrom = 'Parent';
    const caseSubject = 'Content focus request';
    const message = textAreaProps.value;

    return submit({
      uniqueEnrolmentId,
      message,
      caseSubject,
      caseType,
      caseCategory,
      caseSubCategory,
      caseRequestFrom,
      onSubmitSuccess: () => {
        setView(ViewTypes.Success);
      },
    });
  };

  const onRequestContentFocusClickHandler = () => {
    if (!formIsValid()) {
      return setErrors([ErrorTypes.TextAreaMessage]);
    }

    return submitPayloadToSF();
  };

  const renderDefaultView = () => {
    return (
      <ContainerLayout>
        <ShadowContainer className="flex flex-col gap-y-10 !rounded-3xl bg-white p-6 md:!p-20">
          <div className="flex flex-col gap-y-6">
            {submitError && errorMsg && (
              <div className="w-full">
                <ErrorBlock errorMsg={errorMsg} />
              </div>
            )}
            <h1 className="text-2xl font-bold md:text-3xl">What would you like us to focus on?</h1>
            <div className="w-full">
              <p className="mb-2 text-xl">
                Let&apos;s make sure {student}&apos;s {subject} sessions are focused on the right content, concepts and
                topics.
              </p>
              <p>
                Note: If you'd like to provide us with specific material or content, you can upload this during a
                session.
              </p>
            </div>
            <div className="flex w-full flex-col">
              <TextArea {...textAreaProps} className="mb-1" />
              {errors.includes(ErrorTypes.TextAreaMessage) && (
                <span className="my-1 text-xs text-red-5">Please enter a message</span>
              )}
            </div>
            <div className="w-full text-center">
              <div className="tracking-normal">
                <RestrictedButton
                  buttonText="Request content focus"
                  className="w-full md:w-auto"
                  truthyChecks={[!isSubmitting]}
                  onClick={() => {
                    onRequestContentFocusClickHandler();
                  }}
                />
              </div>
            </div>
          </div>
        </ShadowContainer>
      </ContainerLayout>
    );
  };

  const renderSuccessView = () => {
    return (
      <ContainerLayout>
        <ShadowContainer className="flex flex-col items-center gap-6 !rounded-3xl bg-white p-6 md:!p-10">
          <div className="flex flex-col items-center gap-2">
            <div className="flex h-[60px] w-[60px] items-center justify-center rounded-full">
              <CheckIcon className="text-white" width={60} height={60} />
            </div>
            <h1 className="font-merriweather text-[32px] font-bold text-grey-600">Request submitted</h1>
          </div>
          <p className="text-center text-lg text-grey-600">
            We will be in touch to discuss your request and set up your changes.
          </p>
          <Button
            onClick={() => {
              history.push(`${PATH_HUB_ENROLMENT_DETAIL}?token=${enrolmentId}`);
            }}
            size="sm"
            appearance="reverse"
            className="flex h-10 w-auto items-center justify-center !border text-base !font-medium tracking-wide text-grey-400"
          >
            View {student}&apos;s {subject} enrolment
          </Button>
        </ShadowContainer>
      </ContainerLayout>
    );
  };

  const renderEnrolmentLayoutChild = () => {
    switch (view) {
      case ViewTypes.Default:
        return renderDefaultView();
      case ViewTypes.Success:
        return renderSuccessView();
      default:
        return null;
    }
  };

  return (
    <EnrolmentLayout
      sectionHeading={`${student} ${subject}`}
      mainHeading="Content focus request"
      enrolmentId={enrolmentId}
    >
      {renderEnrolmentLayoutChild()}
    </EnrolmentLayout>
  );
};

FormChangeContentFocus.propTypes = {
  selectedEnrolment: PropTypes.shape({
    enrolmentId: PropTypes.string.isRequired,
    student: PropTypes.string.isRequired,
    subject: PropTypes.string.isRequired,
  }).isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  submitError: PropTypes.bool.isRequired,
  errorMsg: PropTypes.string.isRequired,
  submit: PropTypes.func.isRequired,
};

export default connect(
  (state) => {
    return {
      isSubmitting: state.ui.apiState.hubContactPage.isLoading,
      submitError: state.ui.apiState.hubContactPage.fetchError,
      errorMsg: state.ui.apiState.hubContactPage.errorMsg,
    };
  },
  (dispatch) => {
    return {
      submit: bindActionCreators(submitCaseToSF, dispatch),
    };
  }
)(withSelectedEnrolment(FormChangeContentFocus, null, PATH_HUB_CR_CHANGE_CONTENT_FOCUS));

export { FormChangeContentFocus as StatelessFormChangeContentFocus };
